import React, { useCallback, useEffect, useRef } from 'react';
import classes from './SocialProfileSelector.module.scss';
import { RegisterOptions, UseFormReturn, useWatch } from 'react-hook-form';
import { Select } from '../../forms/Select';
import {
  ClickOutsideHandlerFunction,
  SelectDropdownTemplate,
  SelectItem,
  SelectValueTemplate,
  SelectViewProps
} from '../../forms/Select/Select.view';
import { MappedProfileType, TikTokProfileValues } from '../SocialProfileListItem/SocialProfileListItem.view';
import { SocialProfile } from '../SocialProfile';
import { profilesHelpers } from '../../../modules/Settings/Profiles/Profiles.helpers';
import classNames from 'classnames';
import { ListItemContainer } from '../../containers/ListItemContainer';
import { useBreakpoints } from '../../../hooks/useBreakpoints';
import { Modal } from '../../containers/Modal';
import { SocialProfileSelectorList } from '../SocialProfileSelectorList';
import {
  SelectedSocialProfileValue,
  SocialProfileSelectorListWithoutPinterestBoard,
  SocialProfileSelectorListWithPinterestBoard,
  SocialProfileSelectorWithoutTikTokSettings,
  SocialProfileSelectorWithTikTokSettings
} from '../SocialProfileSelectorList/SocialProfileSelectorList.view';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { PostTikTokPrivacy, SocialProfileFieldsFragment } from '../../../models/graphql';
import { SocialProfileSelectorListWithGroups } from '../SocialProfileSelectorListWithGroups';
import { ErrorMessageSeverity } from '../../../constants/form';
import { ErrorRender } from '../../containers/ErrorRender';
import inputClasses from '../../forms/form.module.scss';

export const setProfileSelectorFormValues = (
  sortedProfiles: MappedProfileType[],
  selectedProfiles: SelectedSocialProfileValue[],
  formInstance: UseFormReturn,
  profilesFormFieldName: string
): void => {
  const pnBoardsFormFieldName = `${profilesFormFieldName}_pinterestBoard`; // TODO these should be derived from a helper.
  const tikTokFormFieldName = `${profilesFormFieldName}_tikTok`; // TODO these should be derived from a helper.
  const profilesFormValue: (string | false)[] = [];
  const pnBoardsFormValue: (string | false)[] = [];
  const tikTokFormValue: (TikTokProfileValues | false)[] = [];
  for (let i = 0; i < sortedProfiles.length; i++) {
    const sortedProfileId = sortedProfiles[i].id;
    const matchingSelectedProfile = selectedProfiles.find(p => p.profileId === sortedProfileId);
    if (matchingSelectedProfile) {
      profilesFormValue[i] = matchingSelectedProfile.profileId;
      pnBoardsFormValue[i] = matchingSelectedProfile.pnBoardId || false;
      tikTokFormValue[i] = matchingSelectedProfile.tikTok || false;
    } else {
      profilesFormValue[i] = false;
      pnBoardsFormValue[i] = false;
      tikTokFormValue[i] = false;
    }
  }

  formInstance.setValue(profilesFormFieldName, profilesFormValue);
  formInstance.setValue(pnBoardsFormFieldName, pnBoardsFormValue);
  formInstance.setValue(tikTokFormFieldName, tikTokFormValue);
};

const isTikTokAllowedActionsGuard = (key: string): key is TikTokProfileValues['allowedActions'][number] => {
  return ['comment', 'duet', 'stitch'].includes(key);
};

const isTikTokPromotionGuard = (key: string): key is TikTokProfileValues['promotion'][number] => {
  return ['self', 'other'].includes(key);
};

const isTikTokPostTikTokPrivacyEnumGuard = (key: unknown): key is PostTikTokPrivacy => {
  return Object.values<unknown>(PostTikTokPrivacy).includes(key);
};

const mapTikTokSettingsValue = (data: readonly unknown[] | unknown): (TikTokProfileValues | false)[] | undefined => {
  // value is an object that has the following structure:
  // ${profileId}_${prop}: value. This means that we should split every key to get the value name, and then map it
  // according to the value we get
  if (!Array.isArray(data)) {
    return undefined;
  }
  return data.map(item => {
    if (!item) return false;
    const result: TikTokProfileValues = {
      privacyTarget: null,
      allowedActions: [],
      promotion: []
    };
    for (const [key, value] of Object.entries(item)) {
      if (value && isTikTokAllowedActionsGuard(key) && ['comment', 'duet', 'stitch'].includes(key)) {
        result.allowedActions = Array.from(new Set([...result.allowedActions, key]));
      } else if (value && isTikTokPromotionGuard(key) && ['self', 'other'].includes(key)) {
        result.promotion = Array.from(new Set([...result.promotion, key]));
      } else if (key === 'privacyTarget' && value && isTikTokPostTikTokPrivacyEnumGuard(value)) {
        result.privacyTarget = value;
      }
    }
    return result;
  });
};

export type InputErrorMessageSeveritySocialProfileSelectorType =
  | { errorMessage?: string | JSX.Element; errorSeverity?: never; rules?: RegisterOptions }
  | { errorMessage: string | JSX.Element; errorSeverity: ErrorMessageSeverity; rules?: never };

export type PinterestBoardType =
  | { usePinterestBoard?: true; pinterestRules?: RegisterOptions; pinterestGroupRules?: RegisterOptions }
  | { usePinterestBoard?: false; pinterestRules?: never; pinterestGroupRules?: never };

export type TikTokSettingsType =
  | {
      useTikTokSettings?: true;
      tikTokGroupRules?: RegisterOptions;
      tikTokTargetRules?: RegisterOptions;
      tikTokActionRules?: RegisterOptions;
      tikTokPromotionRules?: RegisterOptions;
    }
  | {
      useTikTokSettings?: false;
      tikTokGroupRules?: never;
      tikTokTargetRules?: never;
      tikTokActionRules?: never;
      tikTokPromotionRules?: never;
    };

export type SocialProfileSelectorViewProps = {
  //
  selectItems: MappedProfileType[];
  useFormInstance: UseFormReturn;
  formFieldName: string;
  label?: string;
  selectedProfiles?: SelectedSocialProfileValue[];
  disabledProfileIds: string[];
  groupByProfile?: boolean;
  selectAllFormName?: string;
  contain?: boolean; //boolean value to determine if the Selector is a dropdown, or a container, e.g. inside the modal
  disabled?: boolean;
  onClose?: VoidFunction;
} & InputErrorMessageSeveritySocialProfileSelectorType &
  PinterestBoardType &
  TikTokSettingsType;
export const SocialProfileSelectorView = (props: SocialProfileSelectorViewProps): JSX.Element => {
  const {
    selectItems,
    formFieldName,
    selectedProfiles,
    useFormInstance,
    contain,
    usePinterestBoard,
    groupByProfile,
    label,
    selectAllFormName,
    errorMessage,
    errorSeverity,
    disabled,
    disabledProfileIds,
    rules,
    pinterestRules,
    pinterestGroupRules,
    useTikTokSettings,
    tikTokTargetRules,
    tikTokActionRules,
    tikTokPromotionRules,
    tikTokGroupRules,
    onClose
  } = props;
  const { control: formControl } = useFormInstance;
  const items = selectItems.map(item => ({ value: item.id, label: item.displayName } as SelectItem));

  const selectedWatch: readonly string[] = useWatch({
    control: formControl,
    name: `${formFieldName}_checkboxes`
  });

  const pnBoardsWatch: readonly string[] = useWatch({
    control: formControl,
    name: `${formFieldName}_pinterestBoard_array`
  });

  const tikTokWatch: readonly unknown[] = useWatch({
    control: formControl,
    name: `${formFieldName}_tikTok_array`
  });

  useEffect(() => {
    const { submitCount } = useFormInstance.formState;
    const currentValue = useFormInstance.getValues(formFieldName);
    const stringifiedCurrentValue = currentValue ? JSON.stringify(currentValue) : '';
    const stringifiedWatch = JSON.stringify(selectedWatch);
    if (stringifiedWatch !== stringifiedCurrentValue && stringifiedWatch !== undefined) {
      // if already tried to submit the form, we want to validate on every change in order to notify user if the error
      // has been fixed
      useFormInstance.setValue(formFieldName, selectedWatch, { shouldValidate: !!submitCount });
    }
  }, [formFieldName, selectedWatch, useFormInstance, selectItems]);

  useEffect(() => {
    const { submitCount } = useFormInstance.formState;
    const currentValue = useFormInstance.getValues(`${formFieldName}_pinterestBoard`);
    const stringifiepnBoardsWatch = JSON.stringify(pnBoardsWatch);
    const stringifiedCurrentValue = currentValue ? JSON.stringify(currentValue) : '';
    if (stringifiepnBoardsWatch !== stringifiedCurrentValue && stringifiepnBoardsWatch !== undefined) {
      // if already tried to submit the form, we want to validate on every change in order to notify user if the error
      // has been fixed
      useFormInstance.setValue(`${formFieldName}_pinterestBoard`, pnBoardsWatch, { shouldValidate: !!submitCount });
    }
  }, [formFieldName, pnBoardsWatch, useFormInstance]);

  useEffect(() => {
    const { submitCount } = useFormInstance.formState;
    const currentValue = useFormInstance.getValues(`${formFieldName}_tikTok`);
    const mappedTikTok = mapTikTokSettingsValue(tikTokWatch);
    const stringifiedTikTokWatch = JSON.stringify(mappedTikTok);
    const stringifiedCurrentValue = currentValue ? JSON.stringify(currentValue) : undefined;
    if (stringifiedTikTokWatch !== stringifiedCurrentValue && stringifiedTikTokWatch !== undefined) {
      // if already tried to submit the form, we want to validate on every change in order to notify user if the error
      // has been fixed
      useFormInstance.setValue(`${formFieldName}_tikTok`, mappedTikTok, { shouldValidate: !!submitCount });
    }
  }, [formFieldName, tikTokWatch, useFormInstance]);

  const profileWrapperClasses = classNames('cell', 'shrink', classes.ProfileWrapper);
  const valueWrapperClasses = classNames('grid-x', classes.ValueOverflow);
  const breakpoint = useBreakpoints();

  const clickOutsideHandler: ClickOutsideHandlerFunction = args => {
    if (breakpoint === 'small' && args.expanded) return;
    args.setExpanded(false);
  };

  const valueTemplate: SelectValueTemplate = useCallback(
    (args: SelectViewProps, globalClasses: string) => {
      const { selectedLabel } = args;
      const overrideGlobalClasses = classNames(globalClasses, classes.ValueTemplate);
      const profiles = selectedWatch ? selectItems.filter(profile => selectedWatch.includes(profile.id)) : [];
      const renderedProfiles = profiles.map((profile, index) => (
        <div className={profileWrapperClasses} key={index}>
          <SocialProfile
            disabled={disabled}
            socialProfile={profilesHelpers.remapSocialProfile(profile, false, false, false)}
            size={'small'}
            {...(profile.__typename !== 'Bitly' ? { image: (profile as SocialProfileFieldsFragment).photoUrl } : {})}
            disconnected={!profile.connected}
          />
        </div>
      ));
      return (
        <div className={profiles.length ? overrideGlobalClasses : globalClasses}>
          {profiles.length ? (
            <div className={valueWrapperClasses}>
              <div className={'cell small-12'}>
                <div className={classes.ValueWrapper}>
                  <div className={'grid-x'}>{renderedProfiles}</div>
                </div>
              </div>
            </div>
          ) : (
            selectedLabel
          )}
        </div>
      );
    },
    [selectedWatch, profileWrapperClasses, selectItems, valueWrapperClasses, disabled]
  );
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownTemplate: SelectDropdownTemplate = useCallback(
    (args, listDropdownClasses) => {
      const { useFormInstance, setExpanded, expanded } = args;

      const validationAction = () => {
        let formNamesArray = usePinterestBoard
          ? [formFieldName, `${formFieldName}_pinterestBoard`, `${formFieldName}_select_all_profiles`]
          : [formFieldName, `${formFieldName}_select_all_profiles`];
        formNamesArray = useTikTokSettings ? [...formNamesArray, `${formFieldName}_tikTok`] : formNamesArray;
        useFormInstance
          .trigger(formNamesArray)
          .then(result => {
            if (result) {
              setExpanded(false);
            }
          })
          .catch(() => {
            //prevents runtime error
          });
      };
      let containerElement: JSX.Element;
      const pinterestOptions:
        | SocialProfileSelectorListWithPinterestBoard
        | SocialProfileSelectorListWithoutPinterestBoard = usePinterestBoard
        ? { pinterestRules: pinterestRules || {}, pinterestFormName: `${formFieldName}_pinterestBoard_array` }
        : {};
      const tikTokOptions: SocialProfileSelectorWithTikTokSettings | SocialProfileSelectorWithoutTikTokSettings =
        useTikTokSettings
          ? {
              tikTokFormName: `${formFieldName}_tikTok_array`,
              tikTokTargetRules: tikTokTargetRules || {},
              tikTokActionRules: tikTokActionRules || {},
              tikTokPromotionRules: tikTokPromotionRules || {}
            }
          : {};
      if (groupByProfile) {
        containerElement = (
          <>
            {usePinterestBoard ? (
              <input
                type={'hidden'}
                readOnly={true}
                {...useFormInstance.register(`${formFieldName}_pinterestBoard`, pinterestGroupRules)}
              />
            ) : (
              <></>
            )}
            {useTikTokSettings ? (
              <input
                type={'hidden'}
                readOnly={true}
                {...useFormInstance.register(`${formFieldName}_tikTok`, tikTokGroupRules)}
              />
            ) : (
              <></>
            )}
            <SocialProfileSelectorListWithGroups
              profiles={selectItems}
              useFormInstance={useFormInstance}
              formFieldName={`${formFieldName}_checkboxes`}
              disabledProfileIds={disabledProfileIds}
              {...tikTokOptions}
              {...pinterestOptions}
              {...(selectedProfiles ? { selectedProfiles } : {})}
              {...(selectAllFormName ? { selectAllFormName } : {})}
            />
          </>
        );
      } else {
        containerElement = (
          <>
            {usePinterestBoard ? (
              <input
                type={'hidden'}
                readOnly={true}
                {...useFormInstance.register(`${formFieldName}_pinterestBoard`, pinterestGroupRules)}
              />
            ) : (
              <></>
            )}
            {useTikTokSettings ? (
              <input
                type={'hidden'}
                readOnly={true}
                {...useFormInstance.register(`${formFieldName}_tikTok`, tikTokGroupRules)}
              />
            ) : (
              <></>
            )}
            <SocialProfileSelectorList
              useFormInstance={useFormInstance}
              formFieldName={`${formFieldName}_checkboxes`}
              profiles={selectItems}
              disabledProfileIds={disabledProfileIds}
              dropdownRef={containerRef}
              dropdownExpanded={expanded}
              {...tikTokOptions}
              {...pinterestOptions}
              {...(selectedProfiles ? { selectedProfiles } : {})}
              {...(selectAllFormName ? { selectAllFormName } : {})}
            />
          </>
        );
      }
      return breakpoint !== 'small' ? (
        <div ref={containerRef} className={listDropdownClasses}>
          <ListItemContainer type={'div'} expanded={expanded} contain={contain}>
            {containerElement}
          </ListItemContainer>
        </div>
      ) : expanded ? (
        <Modal
          size={'small'}
          title={'Select Profile'}
          onClose={validationAction}
          showCloseIcon
          closeOnEsc
          primaryButtonAction={validationAction}
          primaryButtonTitle={'Apply Selection'}
          primaryButtonColor={ButtonColorsEnum.primary}>
          {containerElement}
        </Modal>
      ) : (
        <></>
      );
    },
    [
      usePinterestBoard,
      pinterestRules,
      formFieldName,
      useTikTokSettings,
      tikTokTargetRules,
      tikTokActionRules,
      tikTokPromotionRules,
      groupByProfile,
      breakpoint,
      contain,
      pinterestGroupRules,
      tikTokGroupRules,
      selectItems,
      disabledProfileIds,
      selectedProfiles,
      selectAllFormName
    ]
  );

  const errorClasses = classNames(inputClasses.HelpTextErrorClass, inputClasses.HelpText);

  return (
    <>
      <Select
        clickOutsideHandler={clickOutsideHandler}
        onClose={onClose}
        items={items}
        name={formFieldName}
        rules={rules || {}}
        size={'large'}
        useFormInstance={useFormInstance}
        chooseLabel={'Select Profiles'}
        multiple
        overrideIntent={'mouse'}
        {...(label ? { label } : {})}
        {...(errorMessage && errorSeverity ? { errorMessage, errorSeverity } : {})}
        disabled={disabled}
        valueTemplate={valueTemplate}
        dropdownTemplate={dropdownTemplate}
        selectedValue={undefined}
        deferFormChangeOnSelectedValue={true}
        // IMPORTANT: Set 'selectedValue' to undefined and 'deferFormChangeOnSelectedValue' to true to avoid having
        // the Select component make form value changes which can cause unwanted behavior.
      />
      {!useFormInstance.formState.errors[formFieldName] && (
        <>
          <ErrorRender
            className={errorClasses}
            errors={useFormInstance.formState.errors}
            name={`${formFieldName}_pinterestBoard`}
          />
          <ErrorRender
            className={errorClasses}
            errors={useFormInstance.formState.errors}
            name={`${formFieldName}_tikTok`}
          />
        </>
      )}
    </>
  );
};
